import { Button, Checkbox, Col, Form as AntForm, Input, InputNumber, Row, Select, Upload, DatePicker, TimePicker, AutoComplete } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import isObject from 'lodash/isObject'
import isUndefined from 'lodash/isUndefined'
import get from 'lodash/get'

import { RemoteSelectV2 } from '../remote_select_v2'
import RemoteSelect from './remote_select'

const Form = props => {
  const {
    config,
    errors,
    values,
    onChange,
    onSubmit,
    onError,
    layout = 'horizontal', // ANTD default
    columnLayout,
    sectionLayout,
    submitLayout,
    submitType,
    submitBlock,
    submitText,
    loading,
    children,
    legacy,
    useForm,
    onFieldsChange,
    onValuesChange,
  } = props

  const [formDefault] = AntForm.useForm()
  const form = useForm || formDefault

  // Check: columnLayout || sectionLayout need 'vertical'
  let formLayout = layout

  if (columnLayout || sectionLayout) {
    formLayout = 'vertical'
  }

  // Check: sectionLayout needs columnLayout
  if (sectionLayout && !columnLayout) {
    throw new Error(`sectionLayout prop requires columnLayout prop !`)
  }

  const isFormVertical = formLayout === 'vertical'
  const isFormHorizontal = formLayout === 'horizontal'

  const formSubmitLayout = () => {
    if (submitLayout) {
      return { wrapperCol: submitLayout }
    }

    if (isFormHorizontal) {
      return { wrapperCol: { md: { span: 16, offset: 8 } } }
    }

    if (isFormVertical) {
      return { wrapperCol: { span: 24 } }
    }

    return {}
  }

  const processValues = values => {
    if (legacy) {
      const processed = {}

      Object.keys(values).forEach(key => {
        processed[key] = values[key] === null || values[key] === '' ? undefined : values[key]
      })

      return processed
    }

    const processed = {}

    Object.keys(values).forEach(key => {
      processed[key] = isUndefined(values[key]) ? null : values[key]
    })

    return processed
  }

  const handleSubmit = values => {
    if (onSubmit) {
      onSubmit(processValues(values))
    }
  }

  const handleSubmitFail = errorInfo => {
    if (onError) {
      onError(errorInfo)
    }
  }

  const handleOnChange = field => {
    if (onChange) {
      // handle state update delays
      setTimeout(() => {
        onChange(field, form.getFieldsValue(), form.setFieldsValue)
      }, 1)
    }
  }

  const getSelectItem = itemConfig => {
    const { field, type, placeholder, options, disabled, ...rest } = itemConfig

    const select = mode => (
      <Select
        mode={mode}
        showSearch
        placeholder={placeholder}
        disabled={disabled}
        onChange={() => handleOnChange(field)}
        filterOption={(input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
        options={options.map(option => {
          const isString = !isObject(option)

          return {
            value: isString ? option : option.value,
            label: isString ? option : option.text,
            extra: option,
          }
        })}
        {...rest}
      />
    )

    return select(type)
  }

  const getItem = itemConfig => {
    const { type, placeholder, disabled, field, antdProps, render, ...rest } = itemConfig

    switch (type) {
      case 'remote_select_v2':
        return <RemoteSelectV2 onSearch={itemConfig.onSearch} antd={itemConfig.antd} />
      case 'remoteSelect':
        return <RemoteSelect onChange={() => handleOnChange(field)} itemConfig={itemConfig} />
      case 'select': // This is passed as "mode" to Select component. Such mode does not exist in ANTD !!!
      case 'tags':
      case 'multiple':
        return getSelectItem(itemConfig)
      case 'date':
        return <DatePicker placeholder={placeholder} disabled={disabled} onChange={() => handleOnChange(field)} {...rest} />
      case 'time':
        return <TimePicker onChange={() => handleOnChange(field)} placeholder={placeholder} disabled={disabled} {...antdProps} />
      case 'number':
        return <InputNumber placeholder={placeholder} disabled={disabled} onChange={() => handleOnChange(field)} {...rest} />
      case 'textarea':
        return <Input.TextArea placeholder={placeholder} disabled={disabled} onChange={() => handleOnChange(field)} {...rest} />
      case 'password':
        return <Input.Password placeholder={placeholder} disabled={disabled} onChange={() => handleOnChange(field)} {...rest} />
      case 'checkbox':
        return (
          <Checkbox disabled={disabled} onChange={() => handleOnChange(field)} {...rest}>
            {placeholder}
          </Checkbox>
        )
      case 'file':
        return (
          <Upload name="file" disabled={disabled} {...rest}>
            <Button icon={<UploadOutlined />}>{placeholder}</Button>
          </Upload>
        )
      case 'autocomplete':
        return <AutoComplete placeholder={placeholder} disabled={disabled} onChange={() => handleOnChange(field)} {...rest} />
      case 'custom':
        return render(form, () => handleOnChange(field))
      default:
        return <Input placeholder={placeholder} disabled={disabled} onChange={() => handleOnChange(field)} {...rest} />
    }
  }

  const getFormItem = (itemConfig, isSubItem) => {
    const { label, field, type, rules, props, items, key } = itemConfig

    let formItemProps = { ...props }

    // Add rules to Form.Item props
    formItemProps = {
      ...formItemProps,
      rules,
    }

    // Add errors to From.Item props
    if (errors) {
      const lookup = get(errors, field)

      if (lookup) {
        formItemProps = {
          ...formItemProps,
          help: lookup,
          validateStatus: 'error',
        }
      }
    }

    // Add valuePropName / getValueFromEvent to Form.Item props
    switch (type) {
      case 'checkbox':
        formItemProps = {
          ...formItemProps,
          valuePropName: 'checked',
        }
        break
      case 'file':
        formItemProps = {
          ...formItemProps,
          valuePropName: 'fileList',
          getValueFromEvent: (...args) => {
            return args[0].fileList
          },
        }
        break
      default:
        formItemProps = {
          ...formItemProps,
          valuePropName: 'value',
        }
        break
    }

    const getLabelCol = () => {
      if (!isSubItem) return undefined

      if (isFormHorizontal) return { span: 24 }

      if (isFormVertical) return { span: 23, offset: 1 }

      return {}
    }

    const getWrapperCol = () => {
      if (!isSubItem) return undefined

      if (isFormHorizontal) return { span: 24 }

      if (isFormVertical) return { span: 23, offset: 1 }

      return {}
    }

    // Check "items"
    formItemProps = {
      ...formItemProps,
      key: !items ? field : key,
      label,
      style: { marginBottom: isSubItem ? 0 : undefined },
      labelCol: getLabelCol(),
      wrapperCol: getWrapperCol(),
    }

    if (!items) {
      formItemProps.name = field
    }

    return <AntForm.Item {...formItemProps}>{!items ? getItem(itemConfig) : items.map(subItemConfig => getFormItem(subItemConfig, true))}</AntForm.Item>
  }

  const handleOnFieldsChange = (changedFields, allFields) => {
    if (onFieldsChange) {
      onFieldsChange(changedFields, allFields)
    }
  }

  const handleOnValuesChange = (changedValues, values) => {
    if (onValuesChange) {
      onValuesChange(changedValues, values)
    }
  }

  return (
    <AntForm
      initialValues={values}
      form={form}
      onFinish={handleSubmit}
      onFinishFailed={handleSubmitFail}
      onFieldsChange={handleOnFieldsChange}
      onValuesChange={handleOnValuesChange}
      requiredMark={false}
      layout={formLayout}
      {...(isFormHorizontal && { labelCol: { md: 8 }, wrapperCol: { md: 12 } })}
      style={{
        marginBottom: formLayout !== 'inline' ? '1.5rem' : 0,
      }}
    >
      {/* Horizontal, inline, vertical */}
      {!sectionLayout && !columnLayout && config.filter(item => item.hidden !== true).map(itemConfig => getFormItem(itemConfig))}

      {/* vertical + columns */}
      {!sectionLayout && columnLayout && (
        <Row gutter={32}>
          {config
            .filter(item => item.hidden !== true)
            .map(itemConfig => {
              return (
                <Col {...columnLayout} key={itemConfig.field || itemConfig.key}>
                  {getFormItem(itemConfig)}
                </Col>
              )
            })}
        </Row>
      )}

      {/* vertical + columns + sections */}
      {sectionLayout &&
        columnLayout &&
        config.map((section, index) => {
          const { titleCol, wrapperCol } = sectionLayout
          const { title, config, key } = section

          return (
            <Row key={key || title || index} gutter={[32, 32]} style={{ borderBottom: '1px solid #F0F0F0' }}>
              <Col {...titleCol}>
                <div style={{ fontSize: 16, fontWeight: 'bold' }}>{title}</div>
              </Col>

              <Col {...wrapperCol}>
                <Row gutter={32}>
                  {config
                    .filter(item => item.hidden !== true)
                    .map(itemConfig => {
                      return (
                        <Col {...columnLayout} key={itemConfig.field}>
                          {getFormItem(itemConfig)}
                        </Col>
                      )
                    })}
                </Row>
              </Col>
            </Row>
          )
        })}

      {children}

      <AntForm.Item {...formSubmitLayout()}>
        <Button
          type={submitType || 'primary'}
          htmlType="submit"
          block={submitBlock}
          loading={loading}
          style={{
            marginTop: sectionLayout ? '1.5rem' : 0,
          }}
        >
          {submitText || 'Submit'}
        </Button>
      </AntForm.Item>
    </AntForm>
  )
}

export default Form
